import type { NotificationItem, NotificationNewEvent } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Bell, CheckCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EmptyState, Skeleton, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { fmtAgo } from '../../lib/format';
import { useSocketEvent } from '../../lib/socket';

interface Inbox {
  data: NotificationItem[];
  meta: { page: number; pageSize: number; total: number; unread: number };
}

/** Header bell: unread badge + dropdown inbox; live via `notification.new`. */
export function NotificationsMenu() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language !== 'en';
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const inbox = useQuery({ queryKey: ['notifications'], queryFn: () => api<Inbox>('/notifications?pageSize=20'), refetchInterval: 60_000 });
  const unread = inbox.data?.meta.unread ?? 0;

  useSocketEvent(
    'notification.new',
    useCallback(
      (e: NotificationNewEvent) => {
        void qc.invalidateQueries({ queryKey: ['notifications'] });
        toast.info(ar ? e.titleAr : e.titleEn);
      },
      [qc, toast, ar],
    ),
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (ev: MouseEvent) => {
      if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => ev.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markRead = useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const readAll = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const openItem = (n: NotificationItem) => {
    if (!n.readAt) markRead.mutate(n.id);
    setOpen(false);
    if (n.data?.route) nav(n.data.route);
  };

  return (
    <div className="relative" ref={ref}>
      <button className="btn-secondary relative size-10 !p-0" aria-label={t('header.notifications')} aria-expanded={open} aria-haspopup="dialog" title={t('header.notifications')} onClick={() => setOpen((o) => !o)}>
        <Bell size={18} aria-hidden />
        {unread > 0 ? (
          <span className="absolute -end-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-danger px-1 text-[10px] font-bold text-white" aria-label={t('notifications.unreadCount', { count: unread })}>
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div role="dialog" aria-label={t('header.notifications')} className="absolute end-0 top-12 z-40 w-[min(92vw,380px)] overflow-hidden rounded-2xl border border-border bg-surface shadow-xl">
          <div className="flex items-center justify-between border-b border-border-soft px-4 py-3">
            <h3 className="text-sm font-bold">{t('header.notifications')}</h3>
            <button className="btn-ghost !min-h-8 text-xs" onClick={() => readAll.mutate()} disabled={!unread || readAll.isPending}>
              <CheckCheck size={14} aria-hidden /> {t('notifications.readAll')}
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {inbox.isLoading ? (
              <div className="p-3">
                <Skeleton className="h-24" />
              </div>
            ) : !inbox.data?.data.length ? (
              <EmptyState title={t('notifications.empty')} />
            ) : (
              <ul>
                {inbox.data.data.map((n) => (
                  <li key={n.id}>
                    <button className={clsx('flex w-full flex-col items-start gap-0.5 px-4 py-3 text-start hover:bg-surface-hover', !n.readAt && 'bg-brand-soft/40')} onClick={() => openItem(n)}>
                      <span className="flex w-full items-center gap-2">
                        {!n.readAt ? <span className="size-2 shrink-0 rounded-full bg-brand" aria-hidden /> : null}
                        <span className="flex-1 truncate text-sm font-semibold">{ar ? n.titleAr : n.titleEn}</span>
                        <span className="shrink-0 text-[11px] text-text-faint">{fmtAgo(n.createdAt, i18n.language)}</span>
                      </span>
                      <span className="line-clamp-2 text-xs text-text-2">{ar ? n.bodyAr : n.bodyEn}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
